import * as THREE from 'three';
import type { PlayerMods, SpellDef, SpellStats } from '../game/content';
import { BASE_CRIT, effectiveStats, spellStats } from '../game/rules';
import { SPELL_ELEMENTS, resolveElementalReaction } from '../game/elements';
import { t } from '../i18n';
import type { UiKey } from '../i18n/en.ts';
import type { World } from '../game/World';
import { uploadFirst } from '../utils/gpu';
import type { Enemy } from './Enemies';

export interface OwnedSpell {
  def: SpellDef;
  level: number;
  evolved: boolean;
  slot: number;
  stats: SpellStats;
  color: THREE.Color;
  cd: number;
  damage: number;
  active: number;
  angle: number;
  pulses: number;
  pulseTimer: number;
  tickTimer: number;
}

const enum Kind {
  Missile,
  Bomb,
  Dagger,
  Blade,
  Wave,
}

interface Projectile {
  active: boolean;
  kind: Kind;
  spell: OwnedSpell | null;
  x: number;
  y: number;
  z: number;
  vx: number;
  vz: number;
  sx: number;
  sz: number;
  tx: number;
  tz: number;
  t: number;
  flight: number;
  speed: number;
  damage: number;
  pierce: number;
  radius: number;
  life: number;
  target: Enemy | null;
  targetSerial: number;
  hits: number[];
  returning: boolean;
  spin: number;
}

type ZoneKind = 'pool' | 'burn' | 'vortex' | 'strike' | 'rune';

interface Zone {
  active: boolean;
  kind: ZoneKind;
  spell: OwnedSpell | null;
  x: number;
  z: number;
  r: number;
  damage: number;
  tick: number;
  timer: number;
  life: number;
  delay: number;
  slow: number;
  pull: number;
  fx: number;
}

const MAX_PROJECTILES = 500;
const MAX_ZONES = 90;
const DRAIN_HEAL = 0.3; // HP restored per foe a Blood Siphon tether drains

export class Spells {
  readonly owned: OwnedSpell[] = [];
  readonly root = new THREE.Group();
  private readonly projectiles: Projectile[] = [];
  private readonly zones: Zone[] = [];
  private readonly orbMesh: THREE.InstancedMesh;
  private readonly daggerMesh: THREE.InstancedMesh;
  private readonly bladeMesh: THREE.InstancedMesh;
  private readonly waveMesh: THREE.InstancedMesh;
  private readonly aura: THREE.Mesh;
  private readonly buf: Enemy[] = new Array(420);
  private readonly reactBuf: Enemy[] = new Array(420);
  private readonly visited: number[] = [];
  private readonly isVisited = (e: Enemy) => this.visited.includes(e.serial);
  private readonly matrix = new THREE.Matrix4();
  private readonly quat = new THREE.Quaternion();
  private readonly posV = new THREE.Vector3();
  private readonly scaleV = new THREE.Vector3();
  private readonly color = new THREE.Color();
  private readonly up = new THREE.Vector3(0, 1, 0);
  private readonly tmpColor = new THREE.Color();
  private hitSoundBudget = 0;
  private counts = { orb: 0, dagger: 0, blade: 0, wave: 0 };

  constructor(private readonly world: World) {
    for (let i = 0; i < MAX_PROJECTILES; i += 1) {
      this.projectiles.push({
        active: false, kind: Kind.Missile, spell: null, x: 0, y: 0, z: 0, vx: 0, vz: 0, sx: 0, sz: 0, tx: 0, tz: 0, t: 0, flight: 1,
        speed: 0, damage: 0, pierce: 0, radius: 0.3, life: 0, target: null, targetSerial: 0, hits: [], returning: false, spin: 0,
      });
    }
    for (let i = 0; i < MAX_ZONES; i += 1) {
      this.zones.push({ active: false, kind: 'pool', spell: null, x: 0, z: 0, r: 1, damage: 0, tick: 0.5, timer: 0, life: 0, delay: 0, slow: 0, pull: 0, fx: 0 });
    }
    const basic = () => new THREE.MeshBasicMaterial({ color: '#ffffff' });
    this.orbMesh = this.instanced(new THREE.IcosahedronGeometry(0.5, 1), basic(), 600);
    this.daggerMesh = this.instanced(new THREE.OctahedronGeometry(0.5, 0).scale(0.35, 0.25, 1.6), basic(), 260);
    const blade = new THREE.TorusGeometry(0.7, 0.13, 4, 18, Math.PI * 1.3).rotateX(Math.PI / 2);
    this.bladeMesh = this.instanced(blade, basic(), 60);
    // A crest arc centred on +X, so an instance turned by atan2(-vz, vx) faces where it travels.
    const crest = new THREE.TorusGeometry(1, 0.14, 5, 20, Math.PI * 0.75).rotateX(Math.PI / 2).rotateY(Math.PI * 0.375);
    this.waveMesh = this.instanced(crest, basic(), 40);
    this.aura = new THREE.Mesh(
      new THREE.CircleGeometry(1, 64).rotateX(-Math.PI / 2),
      new THREE.MeshBasicMaterial({ map: auraTexture(), transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, color: '#ffffff' }),
    );
    this.aura.position.y = 0.05;
    this.aura.visible = false;
    this.root.add(this.orbMesh, this.daggerMesh, this.bladeMesh, this.waveMesh, this.aura);
  }

  private instanced(geometry: THREE.BufferGeometry, material: THREE.Material, cap: number): THREE.InstancedMesh {
    const mesh = new THREE.InstancedMesh(geometry, material, cap);
    mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3), 3);
    mesh.frustumCulled = false;
    mesh.count = 0;
    return mesh;
  }

  reset(): void {
    this.owned.length = 0;
    for (const p of this.projectiles) p.active = false;
    for (const z of this.zones) z.active = false;
    this.aura.visible = false;
    this.orbMesh.count = this.daggerMesh.count = this.bladeMesh.count = this.waveMesh.count = 0;
  }

  add(def: SpellDef, mods: PlayerMods): OwnedSpell {
    const spell: OwnedSpell = {
      def, level: 1, evolved: false, slot: this.owned.length, stats: spellStats(def, 1, false), color: new THREE.Color(def.color),
      cd: 0.3, damage: 0, active: 0, angle: 0, pulses: 0, pulseTimer: 0, tickTimer: 0,
    };
    this.owned.push(spell);
    this.refresh(mods);
    return spell;
  }

  evolve(spell: OwnedSpell, mods: PlayerMods): void {
    spell.evolved = true;
    spell.color.set(spell.def.evolution.color);
    spell.active = 0;
    this.refresh(mods);
  }

  refresh(mods: PlayerMods): void {
    for (const s of this.owned) s.stats = effectiveStats(s.def, spellStats(s.def, s.level, s.evolved), mods);
  }

  update(dt: number): void {
    this.hitSoundBudget = Math.min(6, this.hitSoundBudget + dt * 30);
    for (const s of this.owned) this.updateSpell(s, dt);
    this.counts.orb = this.counts.dagger = this.counts.blade = this.counts.wave = 0;
    this.updateProjectiles(dt);
    this.updateZones(dt);
    this.renderOrbits();
    this.finishMeshes();
  }

  // --- damage ---

  private hit(e: Enemy, spell: OwnedSpell, base: number, dirX: number, dirZ: number, force: number): boolean {
    const w = this.world;
    if (!e.alive) return false;
    const crit = w.rng() < BASE_CRIT + w.mods.crit;
    const dmg = base * (crit ? 2 : 1) * (0.9 + w.rng() * 0.2);
    spell.damage += Math.min(dmg, Math.max(0, e.hp));
    if (!w.reducedFx && (crit || dmg >= 25 || Math.random() < 0.55)) w.overlay.number(e.x, 1.4 + e.scale * 0.6, e.z, dmg, crit);
    if (this.hitSoundBudget >= 1) {
      this.hitSoundBudget -= 1;
      w.audio.hit(crit);
    }
    const killed = w.enemies.applyDamage(e, dmg, dirX, dirZ, force);
    if (e.def.behavior !== 'static') {
      const elem = SPELL_ELEMENTS[spell.def.id] ?? 'arcane';
      const react = resolveElementalReaction(e.elemStatus, elem, w.time, dmg);
      if (react.reaction) {
        if (react.vulnDuration > 0 && e.alive) e.vulnUntil = Math.max(e.vulnUntil, w.time + react.vulnDuration);
        if (react.stunDuration > 0 && e.alive) w.enemies.slow(e, 1, react.stunDuration);
        if (!w.reducedFx) {
          w.shapes.ring(e.x, e.z, 0.25, react.aoeRadius, this.tmpColor.set(react.color), 0.35, 2.2);
          w.overlay.text(e.x, 1.85 + e.scale * 0.4, e.z, t(`reaction.${react.reaction}` as UiKey), react.color, 13);
        }
        const n = w.enemies.queryCircle(e.x, e.z, react.aoeRadius, this.reactBuf);
        for (let i = 0; i < n; i += 1) {
          const other = this.reactBuf[i];
          if (!other.alive || other.def.behavior === 'static') continue;
          if (react.spreadVenom) other.elemStatus.venomUntil = Math.max(other.elemStatus.venomUntil, w.time + 4);
          if (react.vulnDuration > 0) other.vulnUntil = Math.max(other.vulnUntil, w.time + react.vulnDuration);
          spell.damage += Math.min(react.bonusDamage, Math.max(0, other.hp));
          w.enemies.applyDamage(other, react.bonusDamage, 0, 0, 4);
        }
      }
    }
    return killed || !e.alive;
  }

  private areaHit(spell: OwnedSpell, x: number, z: number, r: number, damage: number, force: number): number {
    const n = this.world.enemies.queryCircle(x, z, r, this.buf);
    for (let i = 0; i < n; i += 1) {
      const e = this.buf[i];
      const dx = e.x - x;
      const dz = e.z - z;
      const d = Math.hypot(dx, dz) || 1;
      this.hit(e, spell, damage, dx / d, dz / d, force);
    }
    return n;
  }

  // --- casting ---

  private updateSpell(s: OwnedSpell, dt: number): void {
    const w = this.world;
    const st = s.stats;
    const px = w.player.x;
    const pz = w.player.z;
    if (s.def.kind === 'aura') {
      this.aura.visible = true;
      this.aura.position.x = px;
      this.aura.position.z = pz;
      this.aura.rotation.y += dt * 0.6;
      const pulse = Math.max(0, s.tickTimer / st.tick);
      this.aura.scale.setScalar(st.area * (1 + (1 - pulse) * 0.06));
      (this.aura.material as THREE.MeshBasicMaterial).color.copy(s.color).multiplyScalar(0.55 + 0.35 * pulse * pulse);
      s.tickTimer -= dt;
      if (s.tickTimer <= 0) {
        s.tickTimer = st.tick;
        this.areaHit(s, px, pz, st.area, st.damage, st.knockback);
        if (!w.reducedFx) {
          for (let k = 0; k < 6; k += 1) {
            const a = w.rng() * Math.PI * 2;
            const r = Math.sqrt(w.rng()) * st.area;
            w.particles.emit(px + Math.cos(a) * r, 0.1, pz + Math.sin(a) * r, 0, 1.5 + w.rng() * 1.5, 0, this.tmpColor.copy(s.color).multiplyScalar(1.62), 0.35, 0.7, 0, 0.5);
          }
        }
      }
      return;
    }
    if (s.def.kind === 'orbit') {
      if (s.active > 0) {
        s.active -= dt;
        s.angle += st.speed * dt;
        this.orbitHits(s);
        return;
      }
      s.cd -= dt;
      if (s.cd <= 0) {
        s.active = st.duration;
        s.cd = st.cooldown;
        w.audio.cast('orbit');
      }
      return;
    }
    if (s.pulses > 0) {
      s.pulseTimer -= dt;
      if (s.pulseTimer <= 0) {
        s.pulses -= 1;
        s.pulseTimer = 0.22;
        this.nova(s);
      }
    }
    s.cd -= dt;
    if (s.cd > 0) return;
    const fired = this.cast(s);
    s.cd = fired ? st.cooldown : 0.15;
  }

  private cast(s: OwnedSpell): boolean {
    const w = this.world;
    const st = s.stats;
    const px = w.player.x;
    const pz = w.player.z;
    const count = Math.max(1, Math.round(st.count));
    switch (s.def.kind) {
      case 'missile': {
        const first = w.enemies.nearest(px, pz, 17);
        if (!first) return false;
        for (let i = 0; i < count; i += 1) {
          const target = i === 0 ? first : w.enemies.randomNear(px, pz, 12) ?? first;
          const spread = (i - (count - 1) / 2) * 0.5;
          const a = Math.atan2(target.z - pz, target.x - px) + spread;
          const p = this.spawnProjectile(Kind.Missile, s, px, 1.6, pz, Math.cos(a), Math.sin(a), st.speed);
          if (!p) break;
          p.target = target;
          p.targetSerial = target.serial;
          p.pierce = Math.round(st.pierce);
          p.radius = 0.32 * st.area;
          p.life = 3;
        }
        w.audio.cast('missile');
        return true;
      }
      case 'bomb': {
        if (!w.enemies.nearest(px, pz, 14)) return false;
        for (let i = 0; i < count; i += 1) {
          const target = w.enemies.randomNear(px, pz, 12);
          if (!target) break;
          const p = this.spawnProjectile(Kind.Bomb, s, px, 1.6, pz, 0, 0, st.speed);
          if (!p) break;
          p.sx = px;
          p.sz = pz;
          p.tx = target.x + (w.rng() - 0.5);
          p.tz = target.z + (w.rng() - 0.5);
          p.flight = Math.max(0.25, Math.hypot(p.tx - px, p.tz - pz) / st.speed);
          p.radius = 0.45;
          p.life = p.flight + 0.1;
        }
        w.audio.cast('bomb');
        return true;
      }
      case 'nova':
        this.nova(s);
        s.pulses = count - 1;
        s.pulseTimer = 0.22;
        return true;
      case 'chain':
        return this.chain(s, count);
      case 'zone': {
        for (let i = 0; i < count; i += 1) {
          const target = w.enemies.randomNear(px, pz, st.range);
          const a = w.rng() * Math.PI * 2;
          const x = target ? target.x : px + Math.cos(a) * 4;
          const z = target ? target.z : pz + Math.sin(a) * 4;
          this.spawnZone('pool', s, x, z, st.area, st.damage, st.tick, st.duration, 0.25 + i * 0.08, st.slow, 0);
          w.particles.emit(px, 1.6, pz, (x - px) * 2.2, 5, (z - pz) * 2.2, this.tmpColor.copy(s.color).multiplyScalar(1.95), 0.6, 0.45, -12, 0);
        }
        w.audio.cast('zone');
        return true;
      }
      case 'beam': {
        const base = Math.atan2(w.player.faceZ, w.player.faceX);
        for (let i = 0; i < count; i += 1) this.beam(s, base + (i / count) * Math.PI * 2);
        w.audio.cast('beam');
        return true;
      }
      case 'boomerang': {
        const target = w.enemies.nearest(px, pz, 14);
        const base = target ? Math.atan2(target.z - pz, target.x - px) : Math.atan2(w.player.faceZ, w.player.faceX);
        for (let i = 0; i < count; i += 1) {
          const a = base + (i - (count - 1) / 2) * 0.45;
          const p = this.spawnProjectile(Kind.Blade, s, px, 0.9, pz, Math.cos(a), Math.sin(a), st.speed);
          if (!p) break;
          p.radius = 0.75 * st.area;
          p.life = 5;
        }
        w.audio.cast('boomerang');
        return true;
      }
      case 'strike': {
        if (!w.enemies.nearest(px, pz, st.range)) return false;
        this.visited.length = 0;
        for (let i = 0; i < count; i += 1) {
          let target = w.enemies.randomNear(px, pz, st.range);
          for (let tries = 0; target && this.visited.includes(target.serial) && tries < 3; tries += 1) target = w.enemies.randomNear(px, pz, st.range);
          const a = w.rng() * Math.PI * 2;
          const x = target ? target.x : px + Math.cos(a) * 3;
          const z = target ? target.z : pz + Math.sin(a) * 3;
          if (target) this.visited.push(target.serial);
          this.spawnZone('strike', s, x, z, st.area, st.damage, 1, 0.01, 0.35 + i * 0.06, 0, st.knockback);
          w.shapes.ring(x, z, st.area * 0.3, st.area, this.tmpColor.copy(s.color), 0.35 + i * 0.06, 0.7);
        }
        return true;
      }
      case 'volley': {
        const base = Math.atan2(w.player.faceZ, w.player.faceX);
        const dirs = s.evolved ? 2 : 1;
        for (let d = 0; d < dirs; d += 1) {
          for (let i = 0; i < count; i += 1) {
            const a = base + d * Math.PI + (i - (count - 1) / 2) * 0.13;
            const p = this.spawnProjectile(Kind.Dagger, s, px, 1.1, pz, Math.cos(a), Math.sin(a), st.speed);
            if (!p) break;
            p.pierce = Math.round(st.pierce);
            p.radius = 0.3 * st.area;
            p.life = 1.1;
          }
        }
        w.audio.cast('volley');
        return true;
      }
      case 'vortex': {
        for (let i = 0; i < count; i += 1) {
          const target = w.enemies.randomNear(px, pz, st.range);
          const x = target ? target.x : px + w.player.faceX * 5;
          const z = target ? target.z : pz + w.player.faceZ * 5;
          this.spawnZone('vortex', s, x, z, st.area, st.damage, st.tick, st.duration, 0, 0, st.knockback);
        }
        w.audio.cast('vortex');
        return true;
      }
      case 'burst': {
        if (!w.enemies.nearest(px, pz, st.range)) return false;
        const base = w.rng() * Math.PI * 2;
        for (let i = 0; i < count; i += 1) {
          const a = base + (i / count) * Math.PI * 2;
          const p = this.spawnProjectile(Kind.Dagger, s, px, 1.1, pz, Math.cos(a), Math.sin(a), st.speed);
          if (!p) break;
          p.pierce = Math.round(st.pierce);
          p.radius = 0.3 * st.area;
          p.life = st.range / st.speed;
        }
        w.audio.cast('burst');
        return true;
      }
      case 'drain':
        return this.drain(s, count);
      case 'rune': {
        for (let i = 0; i < count; i += 1) {
          const a = w.rng() * Math.PI * 2;
          const r = 1.2 + w.rng() * 2.6;
          this.spawnZone('rune', s, px + Math.cos(a) * r, pz + Math.sin(a) * r, st.area, st.damage, 1, st.duration, 0.35, 0, st.knockback);
        }
        w.audio.cast('rune');
        return true;
      }
      case 'wave': {
        const target = w.enemies.nearest(px, pz, st.range);
        if (!target) return false;
        const base = Math.atan2(target.z - pz, target.x - px);
        for (let i = 0; i < count; i += 1) {
          const a = s.evolved ? base + (i / count) * Math.PI * 2 : base + (i - (count - 1) / 2) * 0.6;
          const p = this.spawnProjectile(Kind.Wave, s, px, 0.45, pz, Math.cos(a), Math.sin(a), st.speed);
          if (!p) break;
          p.pierce = Infinity;
          p.radius = st.area;
          p.life = st.range / st.speed;
        }
        w.audio.cast('wave');
        return true;
      }
      default:
        return false;
    }
  }

  private nova(s: OwnedSpell): void {
    const w = this.world;
    const st = s.stats;
    const px = w.player.x;
    const pz = w.player.z;
    w.shapes.ring(px, pz, 0.4, st.area, s.color, 0.4, 1.6);
    w.shapes.disc(px, pz, 0.4, st.area, s.color, 0.35, 0.3);
    const n = w.enemies.queryCircle(px, pz, st.area, this.buf);
    for (let i = 0; i < n; i += 1) {
      const e = this.buf[i];
      const dx = e.x - px;
      const dz = e.z - pz;
      const d = Math.hypot(dx, dz) || 1;
      if (e.alive) w.enemies.slow(e, st.slow, st.duration);
      this.hit(e, s, st.damage, dx / d, dz / d, st.knockback * 2);
    }
    if (!w.reducedFx) {
      for (let k = 0; k < 28; k += 1) {
        const a = (k / 28) * Math.PI * 2;
        w.particles.emit(px, 0.4, pz, Math.cos(a) * st.area * 3, 0.6, Math.sin(a) * st.area * 3, this.tmpColor.copy(s.color).multiplyScalar(1.04), 0.35, 0.4, 0, 3);
      }
    }
    w.audio.cast('nova');
    if (s.evolved) w.shake(0.12);
  }

  private chain(s: OwnedSpell, count: number): boolean {
    const w = this.world;
    const st = s.stats;
    const px = w.player.x;
    const pz = w.player.z;
    const first = w.enemies.nearest(px, pz, 11);
    if (!first) return false;
    this.visited.length = 0;
    const glow = this.tmpColor.copy(s.color);
    for (let c = 0; c < count; c += 1) {
      let cur: Enemy | null = c === 0 ? first : w.enemies.randomNear(px, pz, 11);
      let fromX = px;
      let fromZ = pz;
      let fromY = 1.9;
      for (let jump = 0; cur && jump <= st.chains; jump += 1) {
        this.visited.push(cur.serial);
        this.bolt(fromX, fromY, fromZ, cur.x, 0.9, cur.z, glow);
        const tx = cur.x;
        const tz = cur.z;
        this.hit(cur, s, st.damage, 0, 0, 0);
        w.particles.burst(tx, 0.9, tz, 4, this.color.copy(glow).multiplyScalar(1.3), 4, 0.3, 0.25, 0.8, 0);
        fromX = tx;
        fromZ = tz;
        fromY = 0.9;
        const n = w.enemies.queryCircle(tx, tz, st.range, this.buf);
        let next: Enemy | null = null;
        let best = Infinity;
        for (let i = 0; i < n; i += 1) {
          const e = this.buf[i];
          if (!e.alive || e.def.behavior === 'static' || this.visited.includes(e.serial)) continue;
          const d = (e.x - tx) ** 2 + (e.z - tz) ** 2;
          if (d < best) {
            best = d;
            next = e;
          }
        }
        cur = next;
      }
    }
    w.audio.cast('chain');
    return true;
  }

  private drain(s: OwnedSpell, count: number): boolean {
    const w = this.world;
    const st = s.stats;
    const px = w.player.x;
    const pz = w.player.z;
    this.visited.length = 0;
    for (let i = 0; i < count; i += 1) {
      const e = w.enemies.nearest(px, pz, st.range, this.isVisited);
      if (!e) break;
      this.visited.push(e.serial);
      w.shapes.beam(px, pz, e.x, e.z, 0.22, s.color, 0.3, 2.6, 1.0);
      // Blood motes fly from the foe to you (no drag, so they arrive as they fade).
      if (!w.reducedFx) for (let k = 0; k < 3; k += 1) w.particles.emit(e.x, 1, e.z, (px - e.x) * 2.5, 0.4 + k * 0.3, (pz - e.z) * 2.5, this.tmpColor.copy(s.color).multiplyScalar(1.6), 0.4, 0.4, 0, 0);
      this.hit(e, s, st.damage, 0, 0, 0);
    }
    if (this.visited.length === 0) return false;
    w.player.heal(DRAIN_HEAL * this.visited.length);
    w.audio.cast('drain');
    return true;
  }

  private bolt(x1: number, y1: number, z1: number, x2: number, y2: number, z2: number, c: THREE.Color): void {
    const w = this.world;
    const segs = 4;
    let ax = x1;
    let az = z1;
    for (let k = 1; k <= segs; k += 1) {
      const t = k / segs;
      const jitter = k === segs ? 0 : 0.45;
      const bx = x1 + (x2 - x1) * t + (w.rng() - 0.5) * jitter;
      const bz = z1 + (z2 - z1) * t + (w.rng() - 0.5) * jitter;
      w.shapes.beam(ax, az, bx, bz, 0.24, c, 0.16, 2.2, y1 + (y2 - y1) * t);
      ax = bx;
      az = bz;
    }
  }

  private beam(s: OwnedSpell, angle: number): void {
    const w = this.world;
    const st = s.stats;
    const px = w.player.x;
    const pz = w.player.z;
    const dx = Math.cos(angle);
    const dz = Math.sin(angle);
    const half = st.area / 2;
    for (const e of w.enemies.active) {
      if (!e.alive) continue;
      const rx = e.x - px;
      const rz = e.z - pz;
      const t = rx * dx + rz * dz;
      if (t < 0 || t > st.range) continue;
      const perp = Math.abs(rx * dz - rz * dx);
      if (perp <= half + e.radius) this.hit(e, s, st.damage, dx, dz, st.knockback);
    }
    const ex = px + dx * st.range;
    const ez = pz + dz * st.range;
    w.shapes.beam(px, pz, ex, ez, st.area * 1.6, s.color, 0.3, 0.45, 0.8);
    w.shapes.beam(px, pz, ex, ez, st.area * 0.45, s.color, 0.22, 2.4, 0.85);
    if (!w.reducedFx) {
      for (let k = 0; k < 14; k += 1) {
        const t = w.rng() * st.range;
        w.particles.emit(px + dx * t, 0.8, pz + dz * t, (w.rng() - 0.5) * 2, 1 + w.rng() * 2, (w.rng() - 0.5) * 2, this.tmpColor.copy(s.color).multiplyScalar(1.95), 0.35, 0.4, 0, 2);
      }
    }
  }

  private orbitHits(s: OwnedSpell): void {
    const w = this.world;
    const st = s.stats;
    const count = Math.max(1, Math.round(st.count));
    const hitR = s.evolved ? 0.8 : 0.6;
    const now = w.time;
    for (let i = 0; i < count; i += 1) {
      const a = s.angle + (i / count) * Math.PI * 2;
      const ox = w.player.x + Math.cos(a) * st.area;
      const oz = w.player.z + Math.sin(a) * st.area;
      const n = w.enemies.queryCircle(ox, oz, hitR, this.buf);
      for (let k = 0; k < n; k += 1) {
        const e = this.buf[k];
        if (e.immune[s.slot] > now) continue;
        e.immune[s.slot] = now + st.tick;
        this.hit(e, s, st.damage, Math.cos(a), Math.sin(a), 3);
      }
      if (!w.reducedFx && w.rng() < 0.5) w.particles.emit(ox, 1.0, oz, 0, 0.3, 0, this.tmpColor.copy(s.color).multiplyScalar(1.3), 0.45, 0.35, 0, 0);
    }
  }

  private renderOrbits(): void {
    const w = this.world;
    for (const s of this.owned) {
      if (s.def.kind !== 'orbit' || s.active <= 0) continue;
      const count = Math.max(1, Math.round(s.stats.count));
      const fade = Math.min(1, s.active / 0.25);
      for (let i = 0; i < count; i += 1) {
        const a = s.angle + (i / count) * Math.PI * 2;
        this.pushOrb(w.player.x + Math.cos(a) * s.stats.area, 1.0, w.player.z + Math.sin(a) * s.stats.area, (s.evolved ? 0.55 : 0.4) * fade, s.color, 1.5);
      }
    }
  }

  // --- projectiles ---

  private spawnProjectile(kind: Kind, s: OwnedSpell, x: number, y: number, z: number, dx: number, dz: number, speed: number): Projectile | null {
    const p = this.projectiles.find((pp) => !pp.active);
    if (!p) return null;
    p.active = true;
    p.kind = kind;
    p.spell = s;
    p.x = x;
    p.y = y;
    p.z = z;
    p.vx = dx * speed;
    p.vz = dz * speed;
    p.speed = speed;
    p.damage = s.stats.damage;
    p.pierce = 1;
    p.t = 0;
    p.target = null;
    p.hits.length = 0;
    p.returning = false;
    p.spin = 0;
    return p;
  }

  private updateProjectiles(dt: number): void {
    const w = this.world;
    const now = w.time;
    for (const p of this.projectiles) {
      if (!p.active || !p.spell) continue;
      const s = p.spell;
      p.life -= dt;
      p.t += dt;
      if (p.kind === Kind.Missile) {
        if (!p.target || !p.target.alive || p.target.serial !== p.targetSerial) {
          p.target = w.enemies.nearest(p.x, p.z, 12, (e) => p.hits.includes(e.serial));
          p.targetSerial = p.target?.serial ?? 0;
        }
        if (p.target) {
          const desired = Math.atan2(p.target.z - p.z, p.target.x - p.x);
          const current = Math.atan2(p.vz, p.vx);
          let diff = desired - current;
          diff = Math.atan2(Math.sin(diff), Math.cos(diff));
          const turn = Math.max(-1, Math.min(1, diff)) * Math.min(1, dt * 9);
          const a = current + turn;
          p.vx = Math.cos(a) * p.speed;
          p.vz = Math.sin(a) * p.speed;
        }
        p.y += (0.9 - p.y) * Math.min(1, dt * 4);
      } else if (p.kind === Kind.Blade) {
        p.spin += dt * 18;
        if (!p.returning) {
          const decel = p.speed / 1.1;
          const v = Math.hypot(p.vx, p.vz);
          const nv = v - decel * dt;
          if (nv <= 0.5) p.returning = true;
          else {
            p.vx *= nv / v;
            p.vz *= nv / v;
          }
        } else {
          const dx = w.player.x - p.x;
          const dz = w.player.z - p.z;
          const d = Math.hypot(dx, dz) || 1;
          const v = Math.min(p.speed * 1.4, Math.hypot(p.vx, p.vz) + p.speed * 2 * dt);
          p.vx = (dx / d) * v;
          p.vz = (dz / d) * v;
          if (d < 0.8) p.active = false;
        }
      }

      if (p.kind === Kind.Bomb) {
        const k = Math.min(1, p.t / p.flight);
        p.x = p.sx + (p.tx - p.sx) * k;
        p.z = p.sz + (p.tz - p.sz) * k;
        p.y = 0.4 + Math.sin(k * Math.PI) * 2.6 + (1 - k) * 1.2;
        if (k >= 1) {
          this.explode(p, s);
          continue;
        }
      } else {
        p.x += p.vx * dt;
        p.z += p.vz * dt;
      }

      if (p.kind !== Kind.Bomb && p.active) {
        const n = w.enemies.queryCircle(p.x, p.z, p.radius, this.buf);
        for (let i = 0; i < n && p.active; i += 1) {
          const e = this.buf[i];
          if (!e.alive) continue;
          const sp = Math.hypot(p.vx, p.vz) || 1;
          if (p.kind === Kind.Blade) {
            if (e.immune[s.slot] > now) continue;
            e.immune[s.slot] = now + s.stats.tick;
            this.hit(e, s, p.damage, p.vx / sp, p.vz / sp, s.stats.knockback);
          } else {
            if (p.hits.includes(e.serial)) continue;
            // A wave only hits what is ahead of its crest, so it never shoves foes behind it into you.
            if (p.kind === Kind.Wave && (e.x - p.x) * p.vx + (e.z - p.z) * p.vz < 0) continue;
            p.hits.push(e.serial);
            this.hit(e, s, p.damage, p.vx / sp, p.vz / sp, p.kind === Kind.Wave ? s.stats.knockback : p.kind === Kind.Dagger ? 1.5 : 2.5);
            p.pierce -= 1;
            if (p.pierce <= 0) {
              p.active = false;
              w.particles.burst(p.x, p.y, p.z, 6, this.tmpColor.copy(s.color).multiplyScalar(1.95), 4, 0.35, 0.25, 0.5, 0);
            }
          }
        }
      }
      if (p.life <= 0) p.active = false;
      if (!p.active) continue;

      // Trails and rendering
      const reduced = w.reducedFx;
      if (p.kind === Kind.Missile || p.kind === Kind.Bomb) {
        const big = p.kind === Kind.Bomb;
        if (w.rng() < (reduced ? 0.15 : 0.5)) {
          w.particles.emit(p.x, p.y, p.z, (w.rng() - 0.5) * 0.6, big ? 0.8 : 0.2, (w.rng() - 0.5) * 0.6, this.tmpColor.copy(s.color).multiplyScalar(big ? 1.5 : 1.3), big ? 0.75 : 0.4, big ? 0.4 : 0.3, big ? 1.5 : 0, 2);
        }
        this.pushOrb(p.x, p.y, p.z, big ? (s.evolved ? 0.6 : 0.45) : 0.28 * s.stats.area, s.color, big ? 1.6 : 1.7);
      } else if (p.kind === Kind.Dagger) {
        if (this.counts.dagger < this.daggerMesh.instanceMatrix.count) {
          this.quat.setFromAxisAngle(this.up, Math.atan2(p.vx, p.vz));
          this.posV.set(p.x, p.y, p.z);
          this.scaleV.setScalar(0.9 * s.stats.area);
          this.matrix.compose(this.posV, this.quat, this.scaleV);
          this.daggerMesh.setMatrixAt(this.counts.dagger, this.matrix);
          this.daggerMesh.setColorAt(this.counts.dagger, this.color.copy(s.color).multiplyScalar(1.3));
          this.counts.dagger += 1;
        }
        if (!reduced && w.rng() < 0.6) w.particles.emit(p.x, p.y, p.z, 0, 0, 0, this.tmpColor.copy(s.color).multiplyScalar(1.04), 0.3, 0.18, 0, 0);
      } else if (p.kind === Kind.Wave) {
        if (this.counts.wave < this.waveMesh.instanceMatrix.count) {
          this.quat.setFromAxisAngle(this.up, Math.atan2(-p.vz, p.vx));
          this.posV.set(p.x, p.y - Math.max(0, 0.25 - p.life) * 2.6, p.z); // sinks into the ground as it dies out
          this.scaleV.set(p.radius, 2.5, p.radius);
          this.matrix.compose(this.posV, this.quat, this.scaleV);
          this.waveMesh.setMatrixAt(this.counts.wave, this.matrix);
          this.waveMesh.setColorAt(this.counts.wave, this.color.copy(s.color).multiplyScalar(1.3));
          this.counts.wave += 1;
        }
        if (!reduced && w.rng() < 0.7) {
          const a = Math.atan2(p.vz, p.vx) + (w.rng() - 0.5) * 2;
          w.particles.emit(p.x + Math.cos(a) * p.radius, 0.6, p.z + Math.sin(a) * p.radius, p.vx * 0.3, 2 + w.rng() * 2, p.vz * 0.3, this.tmpColor.copy(s.color).multiplyScalar(1.7), 0.35, 0.4, -8, 0);
        }
      } else {
        if (this.counts.blade < this.bladeMesh.instanceMatrix.count) {
          this.quat.setFromAxisAngle(this.up, p.spin);
          this.posV.set(p.x, p.y, p.z);
          this.scaleV.setScalar(s.stats.area * (s.evolved ? 1.3 : 1));
          this.matrix.compose(this.posV, this.quat, this.scaleV);
          this.bladeMesh.setMatrixAt(this.counts.blade, this.matrix);
          this.bladeMesh.setColorAt(this.counts.blade, this.color.copy(s.color).multiplyScalar(1.3));
          this.counts.blade += 1;
        }
        if (!reduced) w.particles.emit(p.x + Math.cos(p.spin) * 0.6, p.y, p.z + Math.sin(p.spin) * 0.6, 0, 0.2, 0, this.tmpColor.copy(s.color).multiplyScalar(0.98), 0.35, 0.25, 0, 0);
      }
    }
  }

  private explode(p: Projectile, s: OwnedSpell): void {
    const w = this.world;
    const st = s.stats;
    p.active = false;
    this.areaHit(s, p.tx, p.tz, st.area, p.damage, 4);
    const c = this.tmpColor.copy(s.color);
    w.shapes.disc(p.tx, p.tz, st.area * 0.3, st.area * 1.1, c, 0.3, 0.55);
    w.shapes.ring(p.tx, p.tz, st.area * 0.4, st.area * 1.15, c, 0.3, 1.3);
    w.particles.burst(p.tx, 0.4, p.tz, w.reducedFx ? 8 : 18, this.color.copy(s.color).multiplyScalar(1.17), 6 * Math.sqrt(st.area), 0.45, 0.45, 0.9, -8);
    w.particles.burst(p.tx, 0.3, p.tz, 6, this.color.setRGB(0.35, 0.3, 0.3), 2.5, 1.2, 0.8, 0.6, 1);
    if (st.duration > 0) this.spawnZone('burn', s, p.tx, p.tz, st.area * 0.8, p.damage * 0.2, st.tick, st.duration, 0, 0, 0);
    w.audio.explode(s.evolved ? 0.6 : 0.35);
    w.shake(s.evolved ? 0.14 : 0.06);
  }

  // --- zones ---

  private spawnZone(kind: ZoneKind, s: OwnedSpell, x: number, z: number, r: number, damage: number, tick: number, life: number, delay: number, slow: number, pull: number): void {
    const zone = this.zones.find((zz) => !zz.active);
    if (!zone) return;
    zone.active = true;
    zone.kind = kind;
    zone.spell = s;
    zone.x = x;
    zone.z = z;
    zone.r = r;
    zone.damage = damage;
    zone.tick = tick;
    zone.timer = 0;
    zone.life = life;
    zone.delay = delay;
    zone.slow = slow;
    zone.pull = pull;
    zone.fx = 0;
    if (delay <= 0) this.zoneVisual(zone);
  }

  private zoneVisual(zone: Zone): void {
    const w = this.world;
    const s = zone.spell!;
    if (zone.kind === 'pool' || zone.kind === 'burn') {
      w.shapes.disc(zone.x, zone.z, zone.r * 0.6, zone.r, s.color, zone.life, zone.kind === 'burn' ? 0.55 : 0.4, 1);
      w.shapes.ring(zone.x, zone.z, zone.r, zone.r, s.color, zone.life, 0.6);
    } else if (zone.kind === 'vortex') {
      w.shapes.disc(zone.x, zone.z, zone.r * 1.2, zone.r * 0.6, s.color, zone.life, 0.55, 1, 3);
      w.shapes.ring(zone.x, zone.z, zone.r * 1.25, zone.r * 0.2, s.color, zone.life, 1.1);
    } else if (zone.kind === 'rune') {
      w.shapes.ring(zone.x, zone.z, zone.r * 0.1, zone.r * 0.5, s.color, 0.3, 2.4);
    }
  }

  private updateZones(dt: number): void {
    const w = this.world;
    for (const zone of this.zones) {
      if (!zone.active || !zone.spell) continue;
      const s = zone.spell;
      if (zone.delay > 0) {
        zone.delay -= dt;
        if (zone.delay > 0) continue;
        if (zone.kind === 'strike') {
          this.strike(zone, s);
          zone.active = false;
          continue;
        }
        this.zoneVisual(zone);
        w.particles.burst(zone.x, 0.3, zone.z, 10, this.tmpColor.copy(s.color).multiplyScalar(1.62), 3, 0.5, 0.4, 0.6, -4);
      }
      zone.life -= dt;
      zone.timer -= dt;
      if (zone.kind === 'rune') {
        // Short-lived shapes re-emitted while armed, so the glyph vanishes the moment it fires. Shown even with reduced FX.
        zone.fx -= dt;
        if (zone.fx <= 0) {
          zone.fx = 0.3;
          w.shapes.ring(zone.x, zone.z, zone.r * 0.5, zone.r * 0.5, s.color, 0.32, 1.8);
          w.shapes.disc(zone.x, zone.z, zone.r * 0.42, zone.r * 0.42, s.color, 0.32, 0.5, 1, 2);
        }
        if (zone.life <= 0 || w.enemies.queryCircle(zone.x, zone.z, zone.r * 0.45, this.buf) > 0) {
          zone.active = false;
          this.detonate(zone, s);
        }
        continue;
      }
      if (zone.kind === 'vortex') {
        const pullR = zone.r * 1.35;
        const n = w.enemies.queryCircle(zone.x, zone.z, pullR, this.buf);
        for (let i = 0; i < n; i += 1) {
          const e = this.buf[i];
          if (e.boss || e.def.behavior === 'static') continue;
          const dx = zone.x - e.x;
          const dz = zone.z - e.z;
          const d = Math.hypot(dx, dz);
          if (d < 0.3) continue;
          const pull = (zone.pull * (1 - d / pullR) + 1) * dt;
          e.x += (dx / d) * pull;
          e.z += (dz / d) * pull;
        }
      }
      if (zone.timer <= 0) {
        zone.timer += zone.tick;
        const n = w.enemies.queryCircle(zone.x, zone.z, zone.r, this.buf);
        for (let i = 0; i < n; i += 1) {
          const e = this.buf[i];
          if (zone.slow > 0 && e.alive) w.enemies.slow(e, zone.slow, zone.tick + 0.1);
          this.hit(e, s, zone.damage, 0, 0, 0);
        }
      }
      zone.fx -= dt;
      if (zone.fx <= 0 && !w.reducedFx) {
        zone.fx = zone.kind === 'vortex' ? 0.03 : 0.08;
        const a = w.rng() * Math.PI * 2;
        if (zone.kind === 'vortex') {
          const r = zone.r * 1.3;
          w.particles.emit(zone.x + Math.cos(a) * r, 0.3, zone.z + Math.sin(a) * r, -Math.cos(a) * r * 2 - Math.sin(a) * 4, 0.5, -Math.sin(a) * r * 2 + Math.cos(a) * 4, this.tmpColor.copy(s.color).multiplyScalar(1.62), 0.4, 0.45, 0, 0);
        } else {
          const r = Math.sqrt(w.rng()) * zone.r;
          w.particles.emit(zone.x + Math.cos(a) * r, 0.1, zone.z + Math.sin(a) * r, 0, 1 + w.rng(), 0, this.tmpColor.copy(s.color).multiplyScalar(zone.kind === 'burn' ? 2.5 : 1.8), 0.4, 0.7, 0, 1);
        }
      }
      if (zone.life <= 0) {
        zone.active = false;
        if (zone.kind === 'vortex' && s.evolved) {
          this.areaHit(s, zone.x, zone.z, zone.r, zone.damage * 6, 6);
          w.shapes.ring(zone.x, zone.z, zone.r * 0.2, zone.r * 1.3, s.color, 0.35, 3);
          w.particles.burst(zone.x, 0.5, zone.z, 24, this.tmpColor.copy(s.color).multiplyScalar(1.95), 8, 0.5, 0.4, 0.4, 0);
          w.audio.explode(0.4);
        }
      }
    }
  }

  private detonate(zone: Zone, s: OwnedSpell): void {
    const w = this.world;
    this.areaHit(s, zone.x, zone.z, zone.r, zone.damage, zone.pull);
    w.shapes.disc(zone.x, zone.z, zone.r * 0.3, zone.r * 1.1, s.color, 0.3, 0.6);
    w.shapes.ring(zone.x, zone.z, zone.r * 0.4, zone.r * 1.15, s.color, 0.3, 1.6);
    w.particles.burst(zone.x, 0.4, zone.z, w.reducedFx ? 6 : 14, this.tmpColor.copy(s.color).multiplyScalar(1.6), 5, 0.4, 0.4, 1, -6);
    w.audio.explode(s.evolved ? 0.3 : 0.2);
    if (s.evolved) w.shake(0.04);
  }

  private strike(zone: Zone, s: OwnedSpell): void {
    const w = this.world;
    this.areaHit(s, zone.x, zone.z, zone.r, zone.damage, zone.pull);
    const rock = this.color.copy(s.color).multiplyScalar(0.7);
    const spikes = s.evolved ? 6 : 4;
    for (let k = 0; k < spikes; k += 1) {
      const a = w.rng() * Math.PI * 2;
      const r = w.rng() * zone.r * 0.6;
      w.shapes.spike(zone.x + Math.cos(a) * r, zone.z + Math.sin(a) * r, (1.1 + w.rng() * 0.9) * zone.r, 0.35 + w.rng() * 0.25 * zone.r, rock, 0.55, w.rng() * 6);
    }
    w.particles.burst(zone.x, 0.2, zone.z, w.reducedFx ? 5 : 14, this.tmpColor.setRGB(0.55, 0.45, 0.35), 4, 0.8, 0.6, 1.2, -10);
    w.shapes.ring(zone.x, zone.z, zone.r * 0.5, zone.r * 1.2, s.color, 0.3, 0.9);
    w.audio.cast('strike');
    if (s.evolved) w.shake(0.07);
  }

  // --- mesh helpers ---

  private pushOrb(x: number, y: number, z: number, radius: number, c: THREE.Color, intensity: number): void {
    const i = this.counts.orb;
    if (i >= this.orbMesh.instanceMatrix.count) return;
    this.posV.set(x, y, z);
    this.scaleV.setScalar(radius * 2);
    this.matrix.compose(this.posV, this.quat.identity(), this.scaleV);
    this.orbMesh.setMatrixAt(i, this.matrix);
    this.orbMesh.setColorAt(i, this.color.copy(c).multiplyScalar(intensity));
    this.counts.orb += 1;
  }

  private finishMeshes(): void {
    this.orbMesh.count = this.counts.orb;
    this.daggerMesh.count = this.counts.dagger;
    this.bladeMesh.count = this.counts.blade;
    this.waveMesh.count = this.counts.wave;
    for (const m of [this.orbMesh, this.daggerMesh, this.bladeMesh, this.waveMesh]) {
      m.visible = m.count > 0; // an empty InstancedMesh still costs a draw call
      uploadFirst(m.instanceMatrix, m.count);
      uploadFirst(m.instanceColor, m.count);
    }
  }
}

function auraTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = 256;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(128, 128, 0, 128, 128, 128);
  grad.addColorStop(0, 'rgba(255,255,255,0.05)');
  grad.addColorStop(0.7, 'rgba(255,255,255,0.25)');
  grad.addColorStop(0.95, 'rgba(255,255,255,0.7)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 256, 256);
  g.strokeStyle = 'rgba(255,255,255,0.8)';
  g.lineWidth = 3;
  g.beginPath();
  g.arc(128, 128, 104, 0, Math.PI * 2);
  g.stroke();
  g.font = 'bold 20px serif';
  g.fillStyle = 'rgba(255,255,255,0.9)';
  g.textAlign = 'center';
  const runes = 'ᚠᚢᚦᚨᚱᚲᚷᚹᚺᚾᛁᛃᛇᛈᛉᛊ';
  for (let i = 0; i < runes.length; i += 1) {
    const a = (i / runes.length) * Math.PI * 2;
    g.save();
    g.translate(128 + Math.cos(a) * 88, 128 + Math.sin(a) * 88);
    g.rotate(a + Math.PI / 2);
    g.fillText(runes[i], 0, 7);
    g.restore();
  }
  return new THREE.CanvasTexture(c);
}
